import { useState, type ReactNode } from 'react'
import { Platform, useWindowDimensions, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Typography from '@ui/typography'
import ButtonsGrid from '@ui/buttons/ButtonsFixed/ButtonsGrid'
import Button from '@ui/buttons/Button'
import InsetSheet from '@ui/components/InsetSheet'
import Result from './components/Result'
import Mask, {
  MASK_HEIGHT_LANDSCAPE,
  MASK_HEIGHT_PORTRAIT,
} from './components/ReviewMask'
import {
  resultTypeToVariant,
  REVIEW_MASK_PADDING,
  sContainerHeader,
  sContainerOuter,
} from './styles'
import Manual from './components/ManualAdjustment'
import { ResultValue, Side } from './types'
import { formatOffsetMm } from './format'
import AlertText from '@ui/elements/AlertText'
import { Collapsible } from '@ui/animated/Collapsible'
import cn from '@ui/utils/cn'

interface Props {
  measurement: 'pd' | 'sh'
  results?: {
    r: ResultValue
    l: ResultValue
  }
  /**
   * Advisory shown under the results. The measurement stays usable — this is
   * for a result that is suspect rather than invalid, so it must not read as
   * an error or imply the flow is blocked.
   */
  warning?: string
  /** Makes the result cards tappable, handing back the side that was pressed. */
  onResultPress?: (side: Side) => void
  onSave?: () => void
  onRepeat?: () => void
  onCancel?: () => void
  disabled?: boolean
}

function Content({
  measurement,
  results,
  warning,
  onResultPress,
  onSave,
  onRepeat,
  disabled,
}: Props) {
  const { t } = useTranslation(['measurement'])
  const tNamespace = `measurement:app.review`
  const tl = `${tNamespace}.${measurement}`
  const [showWarning, setShowWarning] = useState(!!warning)

  return (
    <View className={cn(sContainerOuter /*false && 'h-full'*/)}>
      <View className="flex w-full flex-col items-center justify-start gap-8">
        <View className={sContainerHeader}>
          <Typography variant="XLB" className="text-center">
            {t(`${tl}.header.title`)}
          </Typography>
          <Typography
            variant={Platform.select({ default: 'SM', web: 'MD' })}
            className="text-center text-desc"
          >
            {t(`${tl}.header.desc`)}
          </Typography>
        </View>
        {results && (
          <View className="flex w-full flex-col items-center gap-0">
            <View className="flex w-full flex-row items-center justify-center gap-8">
              {results.r && (
                <Result
                  title={t(`${tl}.results.r.label`)}
                  subtitle={t(`${tNamespace}.types.${results.r.type}.label`, {
                    value: formatOffsetMm(results.r.offsetMm ?? 0),
                  })}
                  value={results.r.value}
                  variant={
                    results.r.variant ?? resultTypeToVariant[results.r.type]
                  }
                  disabled={disabled}
                  onPress={onResultPress && (() => onResultPress('r'))}
                />
              )}
              {results.l && (
                <Result
                  title={t(`${tl}.results.l.label`)}
                  subtitle={t(`${tNamespace}.types.${results.l.type}.label`, {
                    value: formatOffsetMm(results.l.offsetMm ?? 0),
                  })}
                  value={results.l.value}
                  variant={
                    results.l.variant ?? resultTypeToVariant[results.l.type]
                  }
                  disabled={disabled}
                  onPress={onResultPress && (() => onResultPress('l'))}
                />
              )}
            </View>
            <Collapsible open={showWarning} fullWidth>
              {warning && (
                <>
                  <View className="pt-8" />
                  <AlertText
                    variant="warning"
                    icon="alert-triangle"
                    text={warning}
                    buttonProps={{
                      label: t('ui:ignore'),
                      onPress: () => setShowWarning(false),
                    }}
                  />
                </>
              )}
            </Collapsible>
          </View>
        )}
      </View>
      <View className="flex w-full flex-row gap-8">
        <ButtonsGrid>
          <Button
            variant="outlined"
            label={t(`${tNamespace}.actions.repeat.label`)}
            disabled={disabled}
            onPress={onRepeat}
          />
          <Button
            variant="filled"
            label={t(`${tNamespace}.actions.continue.label`)}
            disabled={disabled}
            onPress={onSave}
          />
        </ButtonsGrid>
      </View>
      {/* <View className="absolute bottom-0 left-0 h-16 w-full bg-red-500" /> */}
    </View>
  )
}

interface ModalProps extends Props {
  open: boolean
  close?: () => void
  childrenOutside?: ReactNode
  /** Swaps the body for the manual adjustment controls. */
  isManual?: boolean
  /** Manual mode only: per-side offset in mm, as currently applied. */
  offsets?: {
    r: number
    l: number
  }
  /** Manual mode only: side being adjusted. */
  side?: Side
  onSideChange?: (side: Side) => void
  onOffsetChange?: (side: Side, offset: number) => void
}

const Review = {
  Content,
  Mask,
  Modal,
}

function Modal({ open, childrenOutside, isManual, ...props }: ModalProps) {
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  const maskSpace = isLandscape ? MASK_HEIGHT_LANDSCAPE : MASK_HEIGHT_PORTRAIT
  const topSpace = maskSpace + REVIEW_MASK_PADDING * 2

  if (!open) {
    return null
  }

  return (
    <>
      <Mask />
      <InsetSheet
        fullWidth={isManual}
        topSpace={topSpace}
        childrenOutside={childrenOutside}
      >
        {isManual ? (
          <Manual.Content {...props} />
        ) : (
          <Review.Content {...props} />
        )}
      </InsetSheet>
    </>
  )
}

Review.Content = Content
Review.Mask = Mask
Review.Modal = Modal

export default Review
